import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, PlayCircle } from "lucide-react";
import axios from "axios";
import { TESTIMONIALS as FALLBACK } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5 text-amber">
      {Array.from({ length: count }).map((_, i) => (
        <span key={i}>★</span>
      ))}
    </div>
  );
}

function Avatar({ src, name }) {
  const initials = name
    ?.split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="w-8 h-8 rounded-full object-cover border border-ink-600 flex-shrink-0"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-ink-700 border border-ink-600 flex items-center justify-center font-mono text-[10px] text-amber flex-shrink-0">
      {initials || "UM"}
    </div>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState(() =>
    FALLBACK.map((t, i) => ({
      id: `local-${i}`,
      name: t.name,
      role: t.role,
      quote: t.quote,
      rating: 5,
      type: "text",
      avatar: null,
      video_url: null,
    })),
  );
  const [source, setSource] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/testimonials`, {
          params: { limit: 12 },
          timeout: 8000,
        });
        if (cancelled) return;
        if (res.data?.testimonials?.length) {
          setItems(res.data.testimonials);
          setSource(res.data.source || "senja");
        } else {
          setSource("fallback");
        }
      } catch (err) {
        if (!cancelled) setSource("fallback");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      data-testid="testimonials-section"
      data-source={source}
      className="relative bg-ink-900 py-24 md:py-32 border-t border-ink-600"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
          <div>
            <p className="tag-kicker">Members · on the record</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-[1.15] tracking-normal mt-3 max-w-2xl">
              Not testimonials.{" "}
              <span className="italic text-amber">Receipts.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-display text-5xl font-bold">5.0</span>
            <div>
              <div className="flex text-amber text-lg">
                {"★★★★★".split("").map((s, i) => (
                  <span key={i}>{s}</span>
                ))}
              </div>
              <p className="text-xs text-white/50 mt-1 uppercase tracking-widest font-mono">
                25+ reviews · live from senja
              </p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((t, i) => (
            <motion.blockquote
              key={t.id || i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.08 }}
              data-testid={`testimonial-${i}`}
              className="relative p-5 md:p-6 border border-ink-600 bg-ink-800 hover:border-amber/40 transition-colors"
            >
              {t.type === "video" && t.video_url ? (
                <a
                  href={t.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative mb-3 flex items-center gap-2 text-amber hover:opacity-80 transition-opacity"
                  data-testid={`testimonial-video-${i}`}
                >
                  <PlayCircle size={20} />
                  <span className="font-mono text-[10px] uppercase tracking-widest">
                    Video testimonial
                  </span>
                </a>
              ) : (
                <Quote size={18} className="text-amber mb-3" />
              )}

              {t.quote && (
                <p className="font-display text-sm md:text-base leading-snug text-white">
                  "{t.quote}"
                </p>
              )}

              {t.rating && t.rating > 0 && (
                <div className="mt-3 text-xs">
                  <Stars count={Math.min(t.rating, 5)} />
                </div>
              )}

              <footer className="mt-4 pt-3 border-t border-ink-600 flex items-center gap-2.5">
                <Avatar src={t.avatar} name={t.name} />
                <div className="min-w-0">
                  <p className="font-semibold text-xs truncate">{t.name}</p>
                  {t.role && (
                    <p className="text-[10px] text-white/50 uppercase tracking-widest font-mono truncate">
                      {t.role}
                    </p>
                  )}
                </div>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}
